import { ReactNode } from 'react'

import { clsx } from 'clsx'

import { useCopyToClipboard } from '@bettermode/common/hooks'
import { Message } from '@tribeplatform/gql-client/types'
import { useChat } from '@tribeplatform/react-sdk/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { T } from '../../i18n/index.js'
import { useChatContext } from '../ChatContext/ChatContext.js'

import { ChatMessage } from './ChatMessage/ChatMessage.js'

interface Props {
  selectedMessage: Message
  onClose: () => void
  onEditMessage: (message: Message) => void
}

export const SelectedMessageOverlay = ({
  selectedMessage,
  onClose,
  onEditMessage,
}: Props) => {
  const { chatId } = useChatContext()
  const { data: chat } = useChat({
    variables: {
      id: chatId,
    },
    useQueryOptions: {
      enabled: !!selectedMessage && !!chatId,
    },
  })
  const { copied, copyToClipboard } = useCopyToClipboard({
    content: selectedMessage.content,
  })

  const onEdit = () => {
    onEditMessage(selectedMessage)
    onClose()
  }

  if (!selectedMessage) {
    return null
  }
  const canEdit =
    chat?.currentParticipant._id === selectedMessage.authorParticipantId
  const hasContent =
    !!selectedMessage.content && selectedMessage.content !== '<p></p>'

  return (
    <div
      className="w-full h-full absolute inset-0 animate-fade-in backdrop-blur-md flex py-5 justify-center items-center"
      onClick={onClose}
      style={{
        WebkitUserSelect: 'unset',
        WebkitTouchCallout: 'unset',
        userSelect: 'unset',
      }}
    >
      <div className="flex flex-col justify-end items-center overflow-hidden rounded-base p-3 py-2 gap-y-1 max-h-full">
        <div
          className="bg-surface max-w-full sm:max-w-prose rounded-base shadow-base overflow-y-auto no-scrollbar py-2"
          onClick={e => e.stopPropagation()}
        >
          <ChatMessage
            id={selectedMessage._id}
            message={selectedMessage}
            isLastMessage={false}
            isLestReadMessage={false}
            chat={chat}
            layout="compact"
            className="!px-2"
          />
        </div>
        <div className="flex flex-col gap-1 shrink-0 w-full max-w-[10rem]">
          <div className="flex flex-col shadow-base rounded-base bg-surface">
            {canEdit && (
              <ActionItem
                label={<T id="Generics.Edit" defaultMessage="Edit" />}
                onClick={onEdit}
                icon={<SvgIcon name="edit-2" size="lg" />}
              />
            )}
            {canEdit && hasContent && (
              <div className="h-px border-t border-line-subdued" />
            )}
            {hasContent && (
              <ActionItem
                label={<T id="Generics.Copy" defaultMessage="Copy" />}
                onClick={e => {
                  e.stopPropagation()
                  copyToClipboard()
                }}
                icon={<SvgIcon name={copied ? 'check' : 'copy'} size="lg" />}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface ActionItemProps {
  label: ReactNode
  onClick: (e: React.MouseEvent) => void
  icon?: ReactNode
  className?: string
}

export const ActionItem = ({
  label,
  onClick,
  icon,
  className,
}: ActionItemProps) => {
  return (
    <div
      className={clsx(
        'font-medium hover:bg-surface-hovered p-2 flex items-center gap-2 cursor-pointer text-sm transition-colors duration-300 select-none',
        className,
      )}
      onClick={onClick}
    >
      {icon}
      {label}
    </div>
  )
}
